import { useEffect, useRef, useState } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Marquee from './components/Marquee';
import Sensei from './components/Sensei';
import Senpais from './components/Senpais';
import Gimnasio from './components/Gimnasio';
import Galeria from './components/Galeria';
import ShitoRyu from './components/ShitoRyu';
import Contacto from './components/Contacto';
import Footer from './components/Footer';
import bgMusic from './assets/bg-music.mp3';

type AudioState = 'idle' | 'loading' | 'playing' | 'paused';

export default function App() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [volume, setVolume] = useState(0.04);
  const [audioState, setAudioState] = useState<AudioState>('idle');

  useEffect(() => {
    // Force scroll to top on load/reload
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  // Stop the music once the footer comes into view.
  useEffect(() => {
    if (audioState !== 'playing') return;

    const footerEl = document.querySelector('.footer');
    if (!footerEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && audioRef.current) {
          audioRef.current.pause();
          setAudioState('paused');
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(footerEl);

    return () => observer.disconnect();
  }, [audioState]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // The mp3 is only fetched here — on the first click — never on page load.
  const toggleAudio = async () => {
    if (audioState === 'loading') return;

    if (!audioRef.current) {
      setAudioState('loading');
      const audio = new Audio(bgMusic);
      audio.volume = volume;
      audio.addEventListener('ended', () => setAudioState('paused'));
      audioRef.current = audio;
      try {
        await audio.play();
        setAudioState('playing');
      } catch {
        audioRef.current = null;
        setAudioState('idle');
      }
      return;
    }

    if (audioState === 'playing') {
      audioRef.current.pause();
      setAudioState('paused');
    } else {
      await audioRef.current.play();
      setAudioState('playing');
    }
  };

  const label =
    audioState === 'loading'
      ? 'Cargando música'
      : audioState === 'playing'
        ? 'Pausar música'
        : 'Reproducir música';

  return (
    <>
      <Header />
      <Hero />
      <Marquee />
      <Sensei />
      <Senpais />
      <Gimnasio />
      <Galeria />
      <ShitoRyu />
      <Contacto />
      <Footer />

      {/* Background music: loaded on demand, never on page load. */}
      <div className={`volume-control volume-control--${audioState}`}>
        <button
          className="volume-control__btn"
          onClick={toggleAudio}
          title={label}
          aria-label={label}
          aria-busy={audioState === 'loading'}
        >
          {audioState === 'loading' ? (
            <span className="volume-control__spinner" />
          ) : audioState === 'playing' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="var(--red)" />
              {volume >= 0.4 && <path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke="var(--red)" />}
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          className="volume-control__slider"
          aria-label="Volumen de la música"
          title={`Volumen ${Math.round(volume * 100)}%`}
          style={{ '--volume': `${volume * 100}%` } as React.CSSProperties}
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
        />
      </div>
    </>
  );
}
